import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

export interface FileConversionResult {
  file: File;
  fileName: string;
  fileSize: number;
  fileType: string;
  base64Full: string;      // data:mime/type;base64,content
  base64Content: string;   // solo el contenido base64
}

export interface FileValidationConfig {
  maxSizeInMB?: number;
  allowedTypes?: string[];
  allowedExtensions?: string[];
  maxFilenameLength?: number;
}

@Injectable({
  providedIn: 'root'
})
export class FileHandlerService {

  private readonly defaultValidationConfig: FileValidationConfig = {
    maxSizeInMB: 50,
    allowedTypes: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain', 'application/json', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'],
    allowedExtensions: ['.pdf','.doc','.docx','.txt','.md','.json','.xls', '.xlsx', '.csv'],
    maxFilenameLength: 50
  };

  convertFileToBase64(file: File): Observable<FileConversionResult> {
    return new Observable(observer => {
      const reader = new FileReader();
      
      reader.onload = (event: any) => {
        const arrayBuffer = event.target.result;
        const base64Content = this.arrayBufferToBase64(arrayBuffer);
        const base64Full = `data:${file.type};base64,${base64Content}`;
        
        const result: FileConversionResult = {
          file,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          base64Full,
          base64Content
        };
        
        observer.next(result);
        observer.complete();
      };
      
      reader.onerror = (error) => {
        observer.error(new Error(`BASEKNOWLEDGECATALOG.ERRORS.ERROR_READING_FILE`));
      };
      
      reader.readAsArrayBuffer(file);
    });
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 8192;
    let result = '';
    
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.slice(i, i + chunkSize);
      result += String.fromCharCode.apply(null, Array.from(chunk));
    }
    
    return btoa(result);
  }

  convertFilesToBase64(files: File[]): Observable<FileConversionResult[]> {
    return new Observable(observer => {
      const results: FileConversionResult[] = [];
      let completedCount = 0;
      
      if (files.length === 0) {
        observer.next([]);
        observer.complete();
        return;
      }
      
      files.forEach(file => {
        this.convertFileToBase64(file).subscribe({
          next: (result) => {
            results.push(result);
            completedCount++;
            
            if (completedCount === files.length) {
              observer.next(results);
              observer.complete();
            }
          },
          error: (error) => {
            observer.error(error);
          }
        });
      });
    });
  }

  validateFile(file: File, config: FileValidationConfig = this.defaultValidationConfig): { isValid: boolean; error?: string } {
    // Check filename length
    if (config.maxFilenameLength && file.name.length > config.maxFilenameLength) {
      return {
        isValid: false,
        error: `BASEKNOWLEDGECATALOG.ERRORS.FILENAME_TOO_LONG|${config.maxFilenameLength}`
      };
    }

    // Check file size (using base 1000: 1 MB = 1,000,000 bytes to match Windows/C# system)
    if (config.maxSizeInMB && file.size > config.maxSizeInMB * 1000 * 1000) {
      return {
        isValid: false,
        error: `BASEKNOWLEDGECATALOG.ERRORS.FILE_SIZE_EXCEEDS_LIMIT|${config.maxSizeInMB}`
      };
    }

    // Check file extension
    if (config.allowedExtensions && config.allowedExtensions.length > 0) {
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      const isExtensionAllowed = config.allowedExtensions.includes(fileExtension);

      if (!isExtensionAllowed) {
        return {
          isValid: false,
          error: `BASEKNOWLEDGECATALOG.ERRORS.FILE_EXTENSION_NOT_ALLOWED|${fileExtension}`
        };
      }
    }

    return { isValid: true };
  }

  validateFiles(files: File[], config: FileValidationConfig = this.defaultValidationConfig): { file: File; isValid: boolean; error?: string }[] {
    return files.map(file => ({
      file,
      ...this.validateFile(file, config)
    }));
  }

  getFileInfo(file: File): { name: string; size: number; type: string; sizeFormatted: string } {
    return {
      name: file.name,
      size: file.size,
      type: file.type,
      sizeFormatted: this.formatFileSize(file.size)
    };
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    // Using base 1000 (decimal) to match Windows/C# system
    const k = 1000;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  removeFileExtension(fileName: string): string {
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
  }

  getFileExtension(fileName: string): string {
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex > 0 ? fileName.substring(lastDotIndex) : '';
  }

  /**
   * Checks if a file requires a password to open (not write protection or sheet protection)
   * Validation applies only to: PDF (.pdf), Word (.doc, .docx), Excel (.xls, .xlsx)
   * Other file types (.txt, .md, .json, .csv) are not validated
   * 
   * Note: This is a client-side basic check. The backend performs thorough validation with Aspose.
   * 
   * @param file The file to check
   * @returns Observable<boolean> True if the file requires a password to open
   */
  checkIfPasswordProtected(file: File): Observable<boolean> {
    return new Observable(observer => {
      const reader = new FileReader();
      
      reader.onload = (event: any) => {
        const arrayBuffer = event.target.result;
        const isProtected = this.detectPasswordProtection(arrayBuffer, file.name);
        observer.next(isProtected);
        observer.complete();
      };
      
      reader.onerror = () => {
        observer.next(false);
        observer.complete();
      };
      
      reader.readAsArrayBuffer(file);
    });
  }

  private detectPasswordProtection(arrayBuffer: ArrayBuffer, fileName: string): boolean {
    const bytes = new Uint8Array(arrayBuffer);
    const fileExtension = this.getFileExtension(fileName).toLowerCase();
    
    // Only validate password protection for specific file types
    // Allowed extensions: .pdf, .doc, .docx, .txt, .md, .json, .xls, .xlsx, .csv
    // But only PDF, Word, and Excel can have password protection
    
    // Detect password-protected PDFs
    if (fileExtension === '.pdf') {
      return this.isPDFPasswordProtected(bytes);
    }
    
    // Detect password-protected Word documents
    if (fileExtension === '.doc' || fileExtension === '.docx') {
      return this.isOfficePasswordProtected(bytes);
    }
    
    // Detect password-protected Excel files
    if (fileExtension === '.xls' || fileExtension === '.xlsx') {
      return this.isOfficePasswordProtected(bytes);
    }
    
    // Other file types (.txt, .md, .json, .csv, or any other) don't require password validation
    return false;
  }

  private isPDFPasswordProtected(bytes: Uint8Array): boolean {
    try {
      // Convert to string to search for encryption markers
      let content = '';
      const searchLength = Math.min(bytes.length, 50000);
      for (let i = 0; i < searchLength; i++) {
        content += String.fromCharCode(bytes[i]);
      }
      
      // More specific encryption detection to avoid false positives
      // Look for /Encrypt dictionary reference in trailer or catalog
      const hasEncryptRef = /\/Encrypt\s+\d+\s+\d+\s+R/.test(content);
      
      // Look for encryption dictionary with /U and /O keys
      // These should appear as dictionary keys, not as parts of other words
      // Match patterns like: /U( or /U< or /U [ or /U(hex) 
      const hasUserPassword = /\/U\s*[(<\[]/.test(content);
      const hasOwnerPassword = /\/O\s*[(<\[]/.test(content);
      
      // A PDF is password-protected if:
      // 1. It has an /Encrypt reference in the trailer/catalog, OR
      // 2. It has both /U and /O keys in encryption dictionary format
      if (hasEncryptRef || (hasUserPassword && hasOwnerPassword)) {
        return true;
      }
      
      return false;
    } catch {
      return false;
    }
  }

  private isOfficePasswordProtected(bytes: Uint8Array): boolean {
    try {
      // Check ZIP signature (DOCX/XLSX are ZIP files)
      const isZip = bytes[0] === 0x50 && bytes[1] === 0x4B;
      
      if (isZip) {
        // Search for encryption indicators in ZIP header
        // Encrypted ZIP files have bit 0 set in the general purpose bit flag
        // This indicates file-level encryption (password required to open)
        for (let i = 0; i < Math.min(bytes.length - 10, 1000); i++) {
          if (bytes[i] === 0x50 && bytes[i + 1] === 0x4B && 
              bytes[i + 2] === 0x03 && bytes[i + 3] === 0x04) {
            // General purpose bit flag is at offset +6 from local file header
            const flags = bytes[i + 6] | (bytes[i + 7] << 8);
            if (flags & 0x01) { // Bit 0 indicates encryption
              return true;
            }
          }
        }
      }
      
      // Check old Office format (DOC/XLS) - OLE signature
      // Also, encrypted DOCX/XLSX are stored as OLE files
      const isOLE = bytes[0] === 0xD0 && bytes[1] === 0xCF && 
                    bytes[2] === 0x11 && bytes[3] === 0xE0;
      
      if (isOLE) {
        // OLE encrypted files have specific structure with stream names in UTF-16LE
        // Search for encryption markers: "EncryptedPackage" and "EncryptionInfo"
        // These appear as stream names in the OLE structure
        
        // Convert to UTF-16LE string for searching
        // In OLE files, stream names are stored as UTF-16LE (little-endian)
        let utf16Text = '';
        for (let i = 0; i < Math.min(bytes.length - 1, 16384); i += 2) {
          // Read two bytes as UTF-16LE character
          const charCode = bytes[i] | (bytes[i + 1] << 8);
          // Only add printable characters to avoid issues
          if (charCode > 0 && charCode < 65536) {
            utf16Text += String.fromCharCode(charCode);
          }
        }
        
        // Check for strong encryption markers (not just protection markers)
        if (utf16Text.includes('EncryptedPackage') || utf16Text.includes('EncryptionInfo')) {
          return true;
        }
      }
      
      return false;
    } catch {
      return false;
    }
  }
}
